"use client";
import { Check } from "lucide-react";
import ProduktSlider from "@/components/Produkte/ProduktSlider";
import Image, { default as NextImage } from "next/image";
import KundenRezensionenCarousel from "@/components/KundenrezensionenCarousel";

import ImageText from "@/components/ImageText";
import ImageCard from "@/components/ImageCard";
import Section from "@/components/Section";
import { useState } from "react";
import FaqEntry from "@/components/FaqEntry";
import PricingSection from "@/components/PricingSection";

export default function Home() {
  const [showVideo, setShowVideo] = useState(true);

  return (
    <>
      <section className="bg-background py-10 sm:py-14">
        <div className="mx-auto w-full max-w-[1680px] px-3 sm:px-6">
          <ProduktSlider />
        </div>
      </section>

      <Section className="bg-background text-foreground">
        <h2 className="text-center text-4xl">Branchenlösungen</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
          <ImageCard
            imageSrc="/index/products/modeprofi.webp"
            imageAlt="Dein Kassensystem von SDI: So flexibel wie Du selbst."
            title="Modeprofi - Warenwirtschaft"
            description="MODE PROFI - umfassendes Warenwirtschaftssystem für Mode- und Textilunternehmen"
            linkText="Mehr erfahren"
            linkUrl="/modeprofi"
          />
          <ImageCard
            imageSrc="/index/products/Cafe.png"
            imageAlt="Kassensystem Einzelhandel"
            title="Cashtex - Kassensystem"
            description="CashTex ist die moderne Komplettlösung für Kasse und Warenwirtschaft im Einzelhandel."
            linkText="Mehr erfahren"
            linkUrl="/cashtex"
          />
          <ImageCard
            imageSrc="/index/products/office4trade.webp"
            imageAlt="Dein Kassensystem von SDI: So flexibel wie Du selbst."
            title="Office4Trade - Auftragserfassung"
            description="Office4Trade digitalisiert die mobile Auftragserfassung auf Messen und im Außendienst: schnell, einfach, ohne Papier."
            linkText="Mehr erfahren"
            linkUrl="/office4trade"
          />
        </div>
      </Section>
      <PricingSection />
      <Section className="bg-background text-foreground">
        <h2 className="text-center text-4xl mb-6">
          Schnell loslegen mit unserem Kassensystem
        </h2>
        <h3 className="text-center text-xl text-muted-foreground">
          Klicke Dich durch und bekomme einen Vorgeschmack, wie einfach Du
          unsere
          <br />
          Kassensoftware bedienen kannst.
        </h3>

        <div className="mx-auto mt-10 w-full max-w-5xl">
          <div className="relative aspect-video overflow-hidden rounded-2xl border border-black/10 bg-black shadow-sm">
            {showVideo && (
              <NextImage
                onClick={() => setShowVideo(false)}
                src="https://ready2order.com/images/storyland-overlay.svg"
                alt="Kassensystem Demo"
                fill
                className="cursor-pointer object-cover"
                priority
              />
            )}
            <video className="h-full w-full" controls>
              <source src="/VideoFürWebseite.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
      </Section>

      <Section className="bg-[#194172] text-white">
        <h2 className="text-center text-4xl mb-6">
          Jetzt durchstarten – mit dem starken <br /> Kassensystem von SDI!
        </h2>
        <h3 className="text-center text-xl">
          Lass Dich beraten oder starte mit unserer Demo Version.
        </h3>

        <div className="flex flex-row justify-center gap-4 ease-in-out duration-300 mt-10">
          <a
            className="font-display text-center font-medium bg-white text-primary border border-primary
                 w-fit py-4 px-8 hover:shadow-xl cursor-pointer rounded-full transition-all hover:-translate-y-2"
            href="/support?subject=Demo%20Anfrage&body=Guten%20Tag%2C%0A%0Aich%20interessiere%20mich%20für%20eine%20Demo%20des%20Kassensystems%20von%20SDI%20Group.%0A%0ABitte%20nehmen%20Sie%20Kontakt%20mit%20mir%20auf.%0A%0AMit%20freundlichen%20Grüßen"
          >
            Demo anfragen
          </a>
        </div>
      </Section>

      <Section className="bg-background text-foreground dark:bg-slate-900 dark:text-white">
        <h2 className="text-center text-4xl mb-6">Das sagen unsere Kunden:</h2>
        <div className="mt-10">
          <KundenRezensionenCarousel />
        </div>
      </Section>

      <Section className="bg-card text-foreground">
        <ImageText
          imagePosition="left"
          imageSrc="/ECR.png"
          imageAlt="Dein Kassensystem von SDI: So flexibel wie Du selbst."
          title="Dein Kassensystem von SDI: So flexibel wie Du selbst."
        >
          <p>
            CashTex passt überall: Unser Kassensystem-Abo hat alles, was Du im
            Einzelhandel oder als Dienstleister brauchst – standortunabhängig
            und mobil. Und mit unserem Gastro-Paket erweiterst Du diese Basis
            ganz einfach mit allen wichtigen Funktionen für die Gastro mit
            Tischbedienung!
          </p>
          <ul className="ps-8">
            <li>
              <Check className="inline -ms-8 text-[#194172]" />{" "}
              <strong>Ob Handel, Dienstleister oder Gastro:</strong> Wir sind
              das Kassensystem für alle Branchen
            </li>
            <li>
              <Check className="inline -ms-8 text-[#194172]" />{" "}
              <strong>Kassensystem-Abo:</strong> Perfekt für Einzelhandel und
              Dienstleister, vor Ort und unterwegs
            </li>
            <li>
              <Check className="inline -ms-8 text-[#194172]" />{" "}
              <strong>Gastro-Paket:</strong> Ideal für alle Restaurants, Cafés
              und Bars mit Tischbedienung
            </li>
          </ul>
          <div className="flex flex-row gap-4">
            <a
              className="font-display text-center font-medium bg-primary text-primary-foreground border border-primary
                         w-fit py-4 px-8 hover:shadow-xl cursor-pointer rounded-full transition-all hover:-translate-y-2
                         hover:bg-[color:var(--brand-800)]"
              href="/cashtex"
            >
              Zu den Cashtex Basis-Funktionen
            </a>
          </div>
        </ImageText>
      </Section>

      <Section className="bg-background text-foreground">
        <ImageText
          imagePosition="right"
          imageSrc="/ECK.jpg"
          imageAlt="Softwareentwicklung"
          title="1, 2, 3 SDI: So geht Kartenzahlung"
        >
          <p>
            CashTex ist die perfekte Ergänzung für dein Business: Mit dem
            Kartenzahlungsdienst kannst Du über unserem Kassensystem und dem EC
            Terminal ganz einfach bargeldlos kassieren. Akzeptiere alle Karten
            und mobile Payment mit nur 1,23% Gebühr für alle Zahlungsarten –
            einfach, schnell und zuverlässig.
          </p>
          <ul className="ps-8">
            <li>
              <Check className="inline -ms-8 text-[#194172]" />{" "}
              <strong>Ohne Grundgebühr</strong> oder Mindestumsatz
            </li>
            <li>
              <Check className="inline -ms-8 text-[#194172]" />{" "}
              <strong>Alle gängigen Karten und mobile Payments</strong>
            </li>
            <li>
              <Check className="inline -ms-8 text-[#194172]" /> Bis zu{" "}
              <strong>8 Stunden Akkulaufzeit</strong>
            </li>
          </ul>
          <div className="flex flex-row gap-4 ease-in-out duration-300">
            <a
              className="font-display text-center font-medium bg-primary text-primary-foreground border border-primary
                         w-fit py-4 px-8 hover:shadow-xl cursor-pointer rounded-full transition-all hover:-translate-y-2
                         hover:bg-[color:var(--brand-800)]"
              href="/cashtex"
            >
              Kartenzahlung mit CashTex
            </a>
          </div>
        </ImageText>

        <p className="mt-20 px-10">
          <strong>Warum CashTex besonders günstig für Dich ist:</strong>{" "}
          Normalerweise setzen sich die Gebühren für Kartenzahlung aus einer
          fixen Transaktionsgebühr (z.B. 0,09 €) und einem prozentuellen Disagio
          zusammen, wobei für Letzteres unterschiedliche Prozentsätze bei
          Debitkarte und Kreditkarte gelten. Du zahlst also bei jeder
          Kartenzahlung doppelt!
        </p>
        <p className="mb-20 mt-5 px-10">
          <strong>Mit CashTex zahlst Du nur einmal. </strong> Und nur einen
          prozentualen Anteil – keine fixe Gebühr. Das bedeutet, dass Du gerade
          für kleine Summen auch nur wenig Gebühr zahlst. Und bei großen
          Beträgen lohnt sich die günstige Transaktionsgebühr von nur 1,23% erst
          recht!
        </p>

        <div className="payment-logos flex flex-wrap justify-evenly gap-x-7 sm:gap-x-5 gap-y-4">
          <Image
            src="/visa.svg"
            alt="VISA"
            width={32}
            height={32}
            className="h-8 w-auto"
          />
          <Image
            src="/vpay.svg"
            alt="V PAY"
            width={32}
            height={32}
            className="h-8 w-auto"
          />
          <Image
            src="/mastercard.svg"
            alt="Mastercard"
            width={32}
            height={32}
            className="h-8 w-auto"
          />
          <Image
            src="/maestro.svg"
            alt="Maestro"
            width={32}
            height={32}
            className="h-8 w-auto"
          />
          <Image
            src="/amex.svg"
            alt="American Express"
            width={32}
            height={32}
            className="h-8 w-auto"
          />
          <Image
            src="/googlepay.svg"
            alt="Google Pay"
            width={32}
            height={32}
            className="h-8 w-auto"
          />
          <Image
            src="/applepay.svg"
            alt="Apple Pay"
            width={32}
            height={32}
            className="h-8 w-auto"
          />
          <Image
            src="/contactless.svg"
            alt="Kontaktlos bezahlen"
            width={32}
            height={32}
            className="h-8 w-auto"
          />
          <Image
            src="/dinersclub.svg"
            alt="Diners Club"
            width={32}
            height={32}
            className="h-8 w-auto"
          />
        </div>
      </Section>

      <Section className="bg-card text-foreground">
        <ImageText
          imagePosition="left"
          imageSrc="https://web-assets.ready2order.com/17144/1695029838-tse-sicherheit.png"
          imageAlt="Softwareentwicklung"
          title="TSE & Rechtssicherheit: Wir machen es einfach!"
        >
          <p>
            TSE-Pflicht oder neue Anforderungen vom Finanzamt? Kein Problem –
            wir kennen uns aus und übernehmen die Umsetzung der Technischen
            Sicherheitseinrichtung für Dich und informieren Dich über alle
            wichtigen Änderungen.
          </p>
          <ul className="ps-8">
            <li>
              <Check className="inline -ms-8 text-[#194172]" />{" "}
              <strong>Kein zusätzlicher Aufwand:</strong> Regelmäßige und
              kostenlose Updates
            </li>
            <li>
              <Check className="inline -ms-8 text-[#194172]" />{" "}
              <strong>Rechtssicher:</strong> TSE-konform – erfüllt KassenSichV
              und GoBD
            </li>
            <li>
              <Check className="inline -ms-8 text-[#194172]" />{" "}
              <strong>Zuverlässig:</strong> Datensicherheit auf deutschen
              Servern und nach höchsten Standards
            </li>
          </ul>
          <p>
            Mit der Kassensoftware von SDI arbeitest Du immer 100%
            finanzamtkonform und bist automatisch auf der sicheren Seite.
          </p>
        </ImageText>
      </Section>

      <Section className="bg-[#194172] text-white">
        <h2 className="text-center text-4xl mb-6">
          Jetzt beraten lassen – für Deinen Erfolg!
        </h2>
        <h3 className="text-center text-xl">
          Unsere Experten beantworten gern alle Deine Fragen im persönlichen
          <br /> Beratungsgespräch.
        </h3>

        <div className="flex flex-row justify-center gap-4 ease-in-out duration-300 mt-10">
          <a
            className="font-display text-center font-medium bg-white text-primary border border-primary
                       w-fit py-4 px-8 hover:shadow-xl cursor-pointer rounded-full transition-all hover:-translate-y-2"
            href="/support?subject=Demo%20Anfrage&body=Guten%20Tag%2C%0A%0Aich%20interessiere%20mich%20für%20eine%20Demo%20des%20Kassensystems%20von%20SDI%20Group.%0A%0ABitte%20nehmen%20Sie%20Kontakt%20mit%20mir%20auf.%0A%0AMit%20freundlichen%20Grüßen"
          >
            Demo anfragen
          </a>
        </div>
      </Section>

      <section className="py-10 md:py-20 pb-[0.1rem] md:pb-[0.1rem] pt-[0.1rem] md:pt-[0.1rem]">
        <div className="container lg:mx-auto max-w-[1024px] max-[376px]:px-[1rem] min-[376px]:max-sm:px-[1.5rem] sm:max-lg:px-[2rem] lg:max-xl:px-[4rem] xl:max-[1450px]:px-[2.5rem] min-[1450px]:px-0">
          <div className="sharedStyledComponents__StyledRichText-sc-1m43ih4-12 sharedStyledComponents__RichText-sc-1m43ih4-13 iSXBNq richText strong-link font-body text-base">
            <h2 className="mt-20 text-2xl font-bold">
              Was ist ein Kassensystem?
            </h2>
            <p className="mt-5">
              <span style={{ fontWeight: 400 }}>
                Ein Kassensystem ist ein Gerät bzw. eine Lösung, die im
                Einzelhandel, in der Gastronomie und an anderen Verkaufsstellen
                zum Einsatz kommt, um den Kauf- und Zahlungsvorgang abzuwickeln.
              </span>
            </p>
            <p className="mt-5">
              <span style={{ fontWeight: 400 }}>
                Typische Funktionen eines Kassensystems sind:
              </span>
            </p>
            <ul className="mt-5 mb-5">
              <li
                className="mt-5 mb-5"
                style={{ fontWeight: 400 }}
                aria-level={1}
              >
                <b>
                  <Check className="inline text-[#194172]" /> Erfassung von
                  Zahlungen
                </b>
                <span style={{ fontWeight: 400 }}>
                  : Das Kassensystem ermöglicht die Eingabe des zu zahlenden
                  Betrags und berechnet automatisch das Rückgeld.
                </span>
              </li>
              <li
                className="mt-5 mb-5"
                style={{ fontWeight: 400 }}
                aria-level={1}
              >
                <b>
                  <Check className="inline text-[#194172]" /> Ausgabe von
                  Kassenbons
                </b>
                <span style={{ fontWeight: 400 }}>
                  : Nach Abschluss der Transaktion druckt das Kassensystem einen
                  Beleg, der als Nachweis für den Kauf dient.
                </span>
              </li>
              <li
                className="mt-5 mb-5"
                style={{ fontWeight: 400 }}
                aria-level={1}
              >
                <b>
                  <Check className="inline text-[#194172]" /> Erfassung der
                  Einnahmen
                </b>
                <span style={{ fontWeight: 400 }}>
                  : Das Kassensystem speichert die erfassten Umsätze, häufig
                  über ein internes Zählsystem, und ermöglicht es dem Betreiber,
                  den Tagesumsatz sowie einzelne Transaktionen nachzuvollziehen.
                </span>
              </li>
            </ul>

            <p className="mt-5">
              <span style={{ fontWeight: 400 }}>
                Während Kassensysteme ursprünglich als mechanische
                Registrierkassen konzipiert waren, handelt es sich bei modernen
                Lösungen meist um digitale, computergestützte Systeme. Diese
                erweiterten Kassensysteme unterstützen heute zusätzliche
                Funktionen wie die Verarbeitung von Kartenzahlungen, die
                Integration in die Warenwirtschaft sowie das Management von
                Kundendaten.
              </span>
            </p>

            <h2 className="mt-10 mb-5 text-2xl font-bold">
              Was ist der Unterschied zwischen Registrierkasse und Kassensystem?
            </h2>

            <p>
              <span style={{ fontWeight: 400 }}>
                Der wesentliche Unterschied zwischen einer Registrierkasse und
                einem Kassensystem liegt in den verfügbaren Funktionen sowie im
                technologischen Umfang.&nbsp;
              </span>
            </p>

            <ul className="mt-5 mb-5">
              <li
                className="mt-5 mb-5"
                style={{ fontWeight: 400 }}
                aria-level={1}
              >
                <span style={{ fontWeight: 400 }}>
                  <Check className="inline text-[#194172]" />
                  Eine{" "}
                </span>
                <a href="">
                  <b>Registrierkasse</b>
                </a>
                <span style={{ fontWeight: 400 }}>
                  {" "}
                  ist ein einfaches, überwiegend{" "}
                  <strong>hardwarebasiertes Gerät</strong>, das zur
                  Zahlungsabwicklung und zum Drucken von Kassenbons verwendet
                  wird. Sie verfügt in der Regel nur über grundlegende
                  Funktionen wie das Erfassen von Transaktionen und das
                  Berechnen des Wechselgelds.
                </span>
              </li>

              <li
                className="mt-10 mb-10"
                style={{ fontWeight: 400 }}
                aria-level={1}
              >
                <span style={{ fontWeight: 400 }}>
                  <Check className="inline text-[#194172]" /> Ein{" "}
                </span>
                <b>Kassensystem</b>
                <span style={{ fontWeight: 400 }}>
                  {" "}
                  hingegen ist <strong>softwarebasiert </strong>und umfasst
                  nicht nur die Funktionen einer Registrierkasse, sondern bietet
                  darüber hinaus erweiterte Möglichkeiten, beispielsweise zur
                  Verwaltung von Lagerbeständen, Kundendaten, statistischen
                  Auswertungen und steuerlichen Berechnungen. Es lässt sich auf
                  verschiedenen Endgeräten wie PCs, Tablets oder Smartphones
                  einsetzen und kann mit zusätzlicher Hardware wie Bondruckern,
                  Barcodescannern oder Kartenterminals verbunden werden.
                </span>
              </li>
            </ul>

            <p className="mt-10">
              <span style={{ fontWeight: 400 }}>
                <strong>Fazit</strong>: Eine Registrierkasse ist auf die
                grundlegenden Aufgaben der Zahlungsabwicklung und
                Belegerstellung beschränkt, während ein Kassensystem eine
                umfangreichere Funktionalität bietet und die Kombination von
                Software und Hardware zur Unterstützung betrieblicher Abläufe
                ermöglicht.
              </span>
            </p>
          </div>
        </div>
      </section>

      <Section className="bg-card text-foreground">
        <h2 className="text-center text-4xl mb-6">
          FAQ: Alle Antworten zum Kassensystem
        </h2>

        <FaqEntry question="Welche Kassensysteme sind in Deutschland gesetzlich erlaubt?">
          <p>
            Neben elektronischen Kassensystemen sind in Deutschland auch noch
            sogenannte “offene Ladenkassen” zulässig. Diese sind mit einem hohen
            manuellen Aufwand verbunden, um die vorgeschriebenen, allgemeinen
            Aufzeichnungs- und Aufbewahrungspflichten gemäß den Grundsätzen
            ordnungsmäßiger Buchführung (GoBD) zu erfüllen.
          </p>
          <p>
            Für elektronische Kassensysteme gelten die die Anforderungen der
            Kassensicherungsverordnung (KassenSichV), welche vor allem die
            Integration einer zertifizierten Technischen Sicherheitseinrichtung
            (TSE) vorschreibt, um alle Vorgaben zur Manipulationssicherheit
            automatisch zu erfüllen.
          </p>
          <p>
            Grundsätzlich sind alle elektronischen Kassensysteme zulässig, die
            die oben genannten Anforderungen erfüllen. Dazu gehören: POS-Systeme
            (Point of Sale), PC-Kassen, mobile Kassensysteme und moderne
            cloudbasierte Kassensysteme wie auch SDI sie anbietet.
          </p>
        </FaqEntry>

        <FaqEntry
          className="mt-4"
          question="Gibt es in Deutschland eine elektronische Registrierkassenpflicht?"
        >
          <p>
            Nein, in Deutschland besteht derzeit noch keine gesetzliche Pflicht
            zur Nutzung einer elektronischen Registrierkasse. Unternehmen haben
            also die Wahl, ob sie eine elektronische Kasse oder eine offene
            Ladenkasse verwenden möchten.
          </p>
          <p>
            Doch obwohl aktuell keine Pflicht zur Nutzung elektronischer
            Registrierkassen besteht, ist im Koalitionsvertrag der CDU und SPD
            eine mögliche Registrierkassenpflicht ab dem 1. Januar 2027
            vorgesehen. Deren Ziel ist es, Steuerbetrug zu bekämpfen und die
            Steuererhebung zu vereinfachen.
          </p>
        </FaqEntry>

        <FaqEntry
          className="mt-4"
          question="Welche gesetzlichen Anforderungen gelten für Kassensysteme in Deutschland?"
        >
          <p>
            Die Kassensicherungsverordnung (KassenSichV) regelt die
            Anforderungen an elektronische Kassensysteme in Deutschland. Sie
            soll Manipulationen an digitalen Kassendaten verhindern und eine
            ordnungsgemäße, unveränderbare und nachvollziehbare Erfassung aller
            Kassenvorgänge sicherstellen.
          </p>
          <p>
            Die KassenSichV gilt dabei für alle elektronischen oder
            computergestützten Kassensysteme. Offene Ladenkassen (z. B. einfache
            Barkassen ohne elektronische Erfassung) sind nicht betroffen, müssen
            aber trotzdem nach bestimmten Vorgaben nachvollziehbar und
            manipulationssicher dokumentiert werden.
          </p>
          <p>
            Die KassenSichV gilt für alle elektronischen oder computergestützten
            Kassensysteme. Offene Ladenkassen (z. B. einfache Barkassen ohne
            elektronische Erfassung) sind davon ausgenommen, müssen jedoch
            ebenfalls nachvollziehbar und ordnungsgemäß dokumentiert werden.
          </p>
          <p>
            Die Verordnung ist seit dem 1. Januar 2020 in Kraft und umfasst
            insbesondere folgende Pflichten:
          </p>
          <ul>
            <li>
              Technische Sicherheitseinrichtung (TSE): Elektronische
              Kassensysteme müssen mit einer zertifizierten TSE ausgestattet
              sein, die Kassenvorgänge fälschungssicher aufzeichnet.
            </li>
            <li>
              Belegausgabepflicht: Bei jedem Kassenvorgang ist ein Beleg
              auszugeben – gedruckt oder digital. In bestimmten Fällen kann eine
              Befreiung beim Finanzamt beantragt werden.
            </li>
            <li>
              Meldepflicht: Seit dem 1. Januar 2025 müssen elektronische
              Kassensysteme inklusive TSE und Seriennummer beim Finanzamt
              gemeldet werden.
            </li>
            <li>
              Aufbewahrungspflichten (GoBD): Kassendaten sind vollständig,
              unveränderbar und für zehn Jahre digital aufzubewahren.
            </li>
            <li>
              DSFinV-K: Die digitale Schnittstelle der Finanzverwaltung
              ermöglicht standardisierte Kassenprüfungen durch die
              Finanzbehörden.
            </li>
          </ul>
        </FaqEntry>

        <FaqEntry
          className="mt-4"
          question="Wann brauche ich ein TSE-zertifiziertes Kassensystem?"
        >
          <p>
            Ein TSE-zertifiziertes Kassensystem brauchst du immer dann, wenn Du
            in Deutschland ein elektronisches Kassensystem oder eine
            Registrierkasse mit elektronischer Aufzeichnung für Deine
            Barumsätze, EC- oder Kartenzahlungen verwendest – das ist seit dem
            1. Januar 2020 gesetzlich vorgeschrieben.
          </p>
          <p>Ohne TSE geht es nur dann, wenn…</p>
          <ul>
            <li>
              … Du eine offene Ladenkasse (also eine Barkasse ohne elektronische
              Aufzeichnung) nutzt – z. B. eine Geldkassette mit
              handschriftlicher Aufzeichnung. Hier gelten dann die
              Dokumentationspflichten laut GoBD!
            </li>
            <li>
              … Du niemals Bargeld annimmst und kein Kassensystem verwendest (z.
              B. bei reinen Onlineaktionen mit Rechnung).
            </li>
          </ul>
        </FaqEntry>

        <FaqEntry
          className="mt-4"
          question="Was kostet ein Kassensystem in Deutschland?"
        >
          <p>
            Die Kosten für ein Kassensystem in Deutschland variieren stark –
            abhängig von Branche, Funktionsumfang, Anbieter und ob Du Hardware,
            Software oder beides benötigst. Manche Anbieter staffeln ihre Kosten
            auch nach gewünschten Features.
          </p>
          <p>Hier ist eine Übersicht typischer Kosten:</p>
          <ul>
            <li>
              Einfache Kassensysteme z.B. für Kleingewerbe kosten je nach
              Funktionsumfang zwischen 10-40 Euro; dazu kommen Hardwarekosten
              von 100-500 Euro.
            </li>
            <li>
              Mittlere Kassensysteme mit Lager- und Personalwirtschaft kosten
              meist 30-100 Euro pro Monat; für die Hardware können bis zu 1.000
              Euro dazukommen.
            </li>
            <li>
              Komplexe branchen- oder unternehmensspezifische Kassensysteme
              können zwischen 100-300 Euro für maßgeschneiderte Software kosten;
              die Hardware kostet je nach Umfang 2.000-10.000 Euro.
            </li>
          </ul>
          <p>
            Falls nicht bereits enthalten, kommen dazu je nach Anbieter mögliche
            Zusatzkosten für
          </p>
          <ul>
            <li>
              TSE: Cloudlösungen kosten etwa 15 Euro pro Monat;
              Hardware-Lösungen gibt es für einmalig ca. 150 -300 Euro.
            </li>
            <li>Service und Support: meist 5-20 Euro pro Monat</li>
            <li>Schulungen oder Einrichtung: 100 -500 Euro</li>
            <li>Software-Updates (z. B. bei Gesetzesänderungen)</li>
          </ul>
          <p>
            Bei SDI sind Serviceupdates und Support kostenlos. Je nach Branche
            benötigst Du unser Kassensystem-Abo mit oder ohne Gastro-Paket.
            Dabei sind alle Features ohne Aufpreise oder versteckte Kosten
            sofort zugänglich. Wir haben hauseigene Hardware im Angebot, aber Du
            kannst das SDI Kassensystem auch kostensparend als App auf
            vorhandenen Smartphones, Tablets, iPads oder Laptops nutzen. Hier
            erfährst Du mehr über die Preise unseres Kassensystems.
          </p>
        </FaqEntry>

        <FaqEntry
          className="mt-4"
          question="Was ist der Unterschied zwischen einem stationären und einem cloudbasierten Kassensystem?"
        >
          <p>
            Der Unterschied zwischen einem stationären und einem cloudbasierten
            Kassensystem liegt hauptsächlich in der Art der Datenverarbeitung
            und Speicherung – also darin, wo und wie Deine Kassendaten
            gespeichert und verwaltet werden.
          </p>
          <ul>
            <li>
              Beim stationären Kassensystem erfolgt die Datenverarbeitung lokal
              auf einem Gerät (z. B. PC, Kassen-Terminal). Auch die Speicherung
              erfolgt intern, meist auf der Festplatte des Kassensystems. Es ist
              ohne Internetverbindung nutzbar, dafür müssen Updates und
              Datensicherung manuell oder über lokale IT-Lösungen erfolgen.
            </li>
          </ul>
          <p>
            Beim cloudbasierten Kassensystem wie von SDI erfolgt die
            Datenverarbeitung und -speicherung in der Cloud, also über externe
            Server. Das erfordert Internetzugang, ermöglicht aber auch
            automatische Updates ohne Technikerbesuche, zuverlässige
            Datensicherung und Zugriff auf Echtzeit-Daten. Über den externen
            Server kann dieser Zugriff von überall möglich sein (im Geschäft
            oder zuhause und per Login über verschiedene Geräte wie Tablet,
            Laptop, Smartphone). Es ist Ideal für mobile Einsätze,
            Filialbetriebe oder cloudbasierte Buchhaltung.
          </p>
        </FaqEntry>

        <FaqEntry
          className="mt-4"
          question="Brauche ich für mein Kassensystem eine Schulung?"
        >
          <p>
            Viele Kassensysteme erfordern eine Einschulung für die Nutzer*innen.
            Wir von SDI setzen jedoch auf ein intuitives System mit einfacher
            Nutzungsoberfläche, deren Bedienung sehr schnell und selbsterklärend
            erlernt werden kann.
          </p>
          <p>
            So erfolgt die Einschulung in das SDI Kassensystem entweder
            kostenfrei mithilfe der von uns bereitgestellten Materialien (Erste,
            Anleitungen, Hilfecenter, Anleitungen) oder mithilfe zubuchbarer
            Service-Pakete, deren Umfang unsere Kunden selbst wählen.
          </p>
        </FaqEntry>

        <FaqEntry
          className="mt-4"
          question="Muss ich Belege ausdrucken oder reicht ein digitaler Bon?"
        >
          <p>
            In Deutschland gilt seit dem 1. Januar 2020 eine Belegausgabepflicht
            für alle elektronischen Kassensysteme gemäß der
            Kassensicherungsverordnung (KassenSichV). Diese besagt, dass
            Unternehmer ihren Kunden immer einen Beleg ausgeben müssen – und
            zwar unaufgefordert.
          </p>
          <p>Möglich sind gedruckte und digitale Belege:</p>
          <ul>
            <li>
              Papierbeleg: Traditionell wird der Beleg in Papierform
              ausgedruckt. Dies ist nach wie vor häufig, besonders in Geschäften
              mit hoher Kundenfrequenz oder bei Barzahlungen.
            </li>
            <li>
              Digitaler Beleg: Alternativ darf der Beleg auch digital
              übermittelt werden, z. B. per E-Mail oder als QR-Code. Dies ist
              eine umweltfreundlichere Lösung, die vor allem bei Mobile Payment
              beliebt ist.
            </li>
          </ul>
          <p>
            Wichtig: Der Kunde muss die Wahl haben, ob er den Beleg in
            Papierform oder digital erhalten möchte. Wenn der Kunde keinen
            digitalen Beleg verlangt, muss der Beleg ausgedruckt werden können.
          </p>
          <p>
            Es gibt außerdem Ausnahmen (z. B. bei hohem Kundenaufkommen): Wenn
            der Kunde aktiv zustimmt, darf ganz auf die Belegausgabe verzichtet
            werden. Auch das Finanzamt kann im Einzelfall über eine Befreiung
            von der Belegausgabepflicht entscheiden.
          </p>
        </FaqEntry>

        <FaqEntry
          className="mt-4"
          question="Kann ich mein Kassensystem auch für Kartenzahlungen nutzen?"
        >
          <p>
            Mit SDI kannst Du auch Kartenzahlungen und Mobile Payment problemlos
            abwickeln. Mit unserem Zahlungsdienst readyPay und unserer
            hauseigenen Hardware kannst Du sowohl Karten (wie EC- und
            Kreditkarten) als auch Mobile Payment-Optionen wie Apple Pay und
            Google Pay akzeptieren. Diese Integration ermöglicht eine schnelle
            und einfache Zahlungsabwicklung, die nahtlos in Dein Kassensystem
            eingebunden ist. So werden Transaktionen effizient verarbeitet und
            direkt in Deinem System erfasst.
          </p>
          <p>Außerdem profitierst Du von unseren günstigen Konditionen:</p>
          <ul>
            <li>Nur 1,23% Gebühr pro Transaktion</li>
            <li>Für alle gängigen Karten und Mobile Payments</li>
            <li>Ohne Fixkosten, ohne Mindestumsatz</li>
          </ul>
          <p>
            Alternativ kannst Du ein zusätzliches Kartenzahlungsterminal nutzen,
            das Du entweder direkt über SDI oder einen kompatiblen
            Zahlungsanbieter beziehen kannst. Außerdem planen wir die
            Integration verschiedener Zahlungspartner.
          </p>
        </FaqEntry>

        <FaqEntry
          className="mt-4"
          question="Welches Kassensystem eignet sich am besten für meine Branche?"
        >
          <p>
            Das perfekte Kassensystem für Deine Branche hängt von den jeweiligen
            Anforderungen für Gastronomie, Handel oder Dienstleistung ab. So
            beeinflusst die Wahl der Hardware beispielsweise, ob Du mit einem
            stationären Terminal vor Ort oder mobil mit einem tragbaren
            Kassengerät arbeiten möchtest. Auch die Größe Deines Unternehmens,
            die besonderen Anforderungen Deiner Branche und die Komplexität der
            Geschäftsprozesse :.
          </p>
          <p>Wichtige Funktionen für Dein Kassensystem in der Gastronomie</p>
          <p>
            Deine Registrierkasse für die Gastronomie hängt davon ab, ob Du im
            Tischbetrieb am Tisch Bestellungen aufnimmst oder eine Gastronomie
            mit Selbstbedienung und Tresenkasse betreibst. Je nachdem sollte
            Dein Kassensystem bestimmte Funktionen mitbringen:
          </p>
          <ul>
            <li>Funkbonierung direkt an Bar und Küche</li>
            <li>
              Zuverlässige Internetverbindung per WLAN oder integrierter SIM
            </li>
            <li>Beilagen- und Menüfunktion, Happy Hour und Take Away</li>
            <li>Trinkgeldoption</li>
            <li>Tischverwaltung und Tischübersicht</li>
          </ul>
          <p>
            Damit Du hier optimal ausgestattet bist, bieten wir bei ready2order
            ein besonderes Gastro-Paket an.
          </p>
          <p>Wichtige Funktionen für Dein Kasssensystem im Einzelhandel</p>
          <p>
            Deine Registrierkasse für den Einzelhandel sollte die Möglichkeit
            bieten, Zahlungen schnell per Karte, bar oder Mobile Payment
            abzuwickeln. Zusätzlich solltest Du in der Lage sein, Dein Lager in
            Echtzeit zu überwachen, um Verkäufe und Nachbestellungen effizient
            zu steuern. Mit ready2order erhältst Du zudem unter anderem folgende
            Funktionen:
          </p>
          <ul>
            <li>Gutscheine, Rabatte und Aktionen</li>
            <li>Verständliche Statistiken</li>
            <li>Lieferanten- und Kundenmanagement</li>
            <li>Anbindung von Scanner und Kassenlade</li>
            <li>Inventur</li>
          </ul>
          <p>
            Wichtige Funktionen für Dein Kasssensystem in der Dienstleistung
          </p>
          <p>
            Im Servicebereich von Friseur bis Kfz-Werkstatt geht es darum, dass
            Du Deine Verwaltung und Buchhaltung im Griff hast. Und mit
            ready2order hast Du auch im mobilen Einsatz den vollen
            Funktionsumfang:
          </p>
          <ul>
            <li>Erstellung von Angeboten und Rechnungen</li>
            <li>Mitarbeiterverwaltung</li>
            <li>Lieferanten- und Kundenmanagement</li>
            <li>Rabatte und Gutscheine</li>
            <li>Kartenzahlung</li>
          </ul>
        </FaqEntry>
      </Section>

      <Section className="bg-[color:var(--brand-700)] text-white">
        <h2 className="text-center text-4xl mb-6">
          Das flexible Kassensystem für Dein
          <br />
          Unternehmen
        </h2>
        <h3 className="text-center text-xl">
          Lass Dich beraten oder starte gleich mit der kostenlosen Testversion!
        </h3>

        <div className="flex flex-row justify-center gap-4 ease-in-out duration-300 mt-10">
          <a
            className="font-display text-center font-medium bg-white text-primary border border-primary
                       w-fit py-4 px-8 hover:shadow-xl cursor-pointer rounded-full transition-all hover:-translate-y-2"
            href="/support?subject=Demo%20Anfrage&body=Guten%20Tag%2C%0A%0Aich%20interessiere%20mich%20für%20eine%20Demo%20des%20Kassensystems%20von%20SDI%20Group.%0A%0ABitte%20nehmen%20Sie%20Kontakt%20mit%20mir%20auf.%0A%0AMit%20freundlichen%20Grüßen"
          >
            Demo anfragen
          </a>
        </div>
      </Section>
    </>
  );
}
